import React, { lazy, Suspense } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import {
  HATCH_LEVER_JOBS_URL,
  ZENDESK_SUPPORT_PAGE_URL,
} from "./utils/constants";
import { CUSTOM_PRODUCT_PAGE_HANDLES } from "./utils/productConstants";
import { retry } from "./utils/routes";
import {
  builderIsEnabled,
  sleepStoreIsEnabled,
  blogIsEnabled,
} from "./utils/general";
import {
  areRestMiniPagesEnabled,
  isGiftCardPageEnabled,
} from "./utils/enabledFeatureChecks";
import { communityLandingPageIsEnabled } from "./config";

// Components
import LoadingSpinner from "./components/Atoms/LoadingSpinner";
import LazyLoadErrorBoundary from "./components/Atoms/LazyLoadErrorBoundary";

let additionalRoutes = [];

const REST_MINI_ENABLED = areRestMiniPagesEnabled();
const GIFT_CARD_PDP_ENABLED = isGiftCardPageEnabled();
const PAGE_COMPONENT_PATH = "./components/Pages";
const NEW_PAGE_COMPONENT_PATH = "./new-components/pages";

const RestMiniHomepage = REST_MINI_ENABLED
  ? lazy(() =>
      retry(() =>
        import(
          `${PAGE_COMPONENT_PATH}/RestMiniHomepage` /* webpackChunkName: "home" */
        )
      )
    )
  : lazy(() =>
      retry(() =>
        import(
          `${PAGE_COMPONENT_PATH}/RestoreHomePage` /* webpackChunkName: "home" */
        )
      )
    );
const ResetPasswordPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/ResetPasswordPage` /* webpackChunkName: "reset-password" */
    )
  )
);
const HatchMembershipPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/HatchMembershipLandingPage` /* webpackChunkName: "hatch-membership" */
    )
  )
);
const AccessoriesPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/AccessoriesPage` /* webpackChunkName: "accessories" */
    )
  )
);
const RestFamilyPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/RestFamilyPage` /* webpackChunkName: "rest-family" */
    )
  )
);

// Company Pages
const AboutUsPage = lazy(() =>
  retry(() =>
    import(`${PAGE_COMPONENT_PATH}/AboutUsPage` /* webpackChunkName: "about" */)
  )
);
const CareersPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/CareersPage` /* webpackChunkName: "career" */
    )
  )
);
const PrivacyPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/PrivacyPage` /* webpackChunkName: "privacy" */
    )
  )
);
const TermsPage = lazy(() =>
  retry(() =>
    import(`${PAGE_COMPONENT_PATH}/TermsPage` /* webpackChunkName: "terms" */)
  )
);

// Product Pages
const RestoreHomePage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/RestoreHomePage` /* webpackChunkName: "home" */
    )
  )
);
const Rest1stGenPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/Rest1stGenProductPage` /* webpackChunkName: "rest-1st-gen" */
    )
  )
);
const Rest2ndGenPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/RestProductPage` /* webpackChunkName: "rest-2nd-gen" */
    )
  )
);
const RestPlusPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/RestPlusProductPage` /* webpackChunkName: "rest-plus" */
    )
  )
);

if (GIFT_CARD_PDP_ENABLED) {
  const GiftCardPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/GiftCardPage` /* webpackChunkName: "gift-card" */
      )
    )
  );

  additionalRoutes = [
    ...additionalRoutes,
    <Route
      key="hatch-gift-cards"
      path="/hatch-gift-cards"
      component={GiftCardPage}
    />,
  ];
}

if (communityLandingPageIsEnabled) {
  const CommunityLandingPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/CommunityLandingPage` /* webpackChunkName: "community-landing" */
      )
    )
  );

  additionalRoutes = [
    ...additionalRoutes,
    <Route
      key="community-landing"
      path="/hatch-community"
      component={CommunityLandingPage}
    />,
  ];
}

if (REST_MINI_ENABLED) {
  const RestMiniProductPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/RestMiniProductPage` /* webpackChunkName: "rest-mini-pdp" */
      )
    )
  );

  additionalRoutes = [
    ...additionalRoutes,
    <Route
      key="rest-mini-pdp"
      path="/rest-mini"
      component={RestMiniProductPage}
    />,
  ];
}

// Sleep Store Pages
if (sleepStoreIsEnabled()) {
  // const DreamShopLandingPage = lazy(() => retry(() => import(`${PAGE_COMPONENT_PATH}/DreamShopLandingPage` /* webpackChunkName: "dream-shop-landing" */)))
  const DreamShopProductPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/DreamShopProductPage` /* webpackChunkName: "dream-shop-product" */
      )
    )
  );
  const DreamShopCollectionPageCompare = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/DreamShopCollectionPageCompare` /* webpackChunkName: "dream-shop-collection" */
      )
    )
  );
  const RestoreProductPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/WhoopRestoreProductPage` /* webpackChunkName: "restore" */
      )
    )
  );
  const RestoreShopPage = lazy(() =>
    retry(() =>
      import(
        `${NEW_PAGE_COMPONENT_PATH}/restore/restore-shop` /* webpackChunkName: "restore" */
      )
    )
  );
  const RestoreBenefitPage = lazy(() =>
    retry(() =>
      import(
        `${NEW_PAGE_COMPONENT_PATH}/restore/restore-benefits` /* webpackChunkName: "restore" */
      )
    )
  );
  const ShopAllPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/ShopAllPage/Index` /* webpackChunkName: "restore" */
      )
    )
  );
  // const WhoopRestoreProductPage = lazy(() => retry(() => import(`${PAGE_COMPONENT_PATH}/WhoopRestoreProductPage` /* webpackChunkName: "restore" */)))

  additionalRoutes = [
    ...additionalRoutes,
    <Route
      key="dream-shop-collection"
      path="/dream-shop/collections/:collectionSlug"
      component={DreamShopCollectionPageCompare}
    />,

    <Route
      key="dream-shop-product-detail"
      path="/dream-shop/:productSlug/:variantSlug?"
      render={({ match: { params } }) => {
        // Don't render dream shop product page for products with custom product pages
        if (CUSTOM_PRODUCT_PAGE_HANDLES.includes(params.productSlug)) {
          return <CatchAllPage />;
        }
        if (
          params.productSlug === "coverlets" ||
          params.productSlug === "rest-travel-case" ||
          params.productSlug === "hatch-cloud-pillow" ||
          params.productSlug === "baby-milestone-cards"
        ) {
          return <DreamShopProductPage />;
        }

        return <Redirect to="/" />;
      }}
    />,

    // <Route
    //   exact
    //   key='dream-shop-landing'
    //   path='/dream-shop'
    //   component={DreamShopLandingPage} />,
    <Redirect key="dream-shop-landing" from="/dream-shop" to="/" />,

    <Redirect
      key="accessories-landing"
      from="/accessories"
      to="/accessories/powercords"
    />,

    <Route
      exact
      key="restore-product"
      path="/restore"
      component={RestoreProductPage}
    />,
    // <Route
    //   exact
    //   key='restorehsm-product'
    //   path='/restore'
    //   component={RestoreProductPage} />,

    <Route
      exact
      key="restore-shop"
      path="/restore-shop"
      component={RestoreShopPage}
    />,

    <Route
      exact
      key="restore-benefits"
      path="/restore-benefits"
      component={RestoreBenefitPage}
    />,

    <Route exact key="shop-all" path="/shop-all" component={ShopAllPage} />,
  ];
}

// Blog Post Pages
if (blogIsEnabled()) {
  const BlogLandingPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/BlogLandingPage` /* webpackChunkName: "blog-landing" */
      )
    )
  );
  const BlogPostDetailPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/BlogPostDetailPage` /* webpackChunkName: "blog-post" */
      )
    )
  );

  additionalRoutes = [
    ...additionalRoutes,
    <Route exact key="blog-landing" path="/blog" component={BlogLandingPage} />,

    <Route
      key="blog-post-detail"
      path="/blog/:postSlug"
      component={BlogPostDetailPage}
    />,
  ];
}

// Builder.io Pages
let CatchAllPage = null;
if (builderIsEnabled()) {
  CatchAllPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/BuilderIOCatchAllPage` /* webpackChunkName: "catch-all-page" */
      )
    )
  );
} else {
  CatchAllPage = lazy(() =>
    retry(() =>
      import(
        `${PAGE_COMPONENT_PATH}/PageNotFound` /* webpackChunkName: "not-found" */
      )
    )
  );
}

const LinkedInCorporateWellnessPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/LinkedInCorporateWellnessPage` /* webpackChunkName: "linked-in-wellness-page" */
    )
  )
);

const DeepLinkLandingPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/DeepLinkLandingPage` /* webpackChunkName: "deep-link-landing-page" */
    )
  )
);

const HatchForWorkPage = lazy(() =>
  retry(() =>
    import(
      `${PAGE_COMPONENT_PATH}/HatchForWorkPage` /* webpackChunkName: "hatch-for-work-page" */
    )
  )
);

const Routes = (props) => {
  return (
    <LazyLoadErrorBoundary>
      <Suspense fallback={<LoadingSpinner />}>
        <Switch>
          <Route exact key="home-page" path="/" component={RestMiniHomepage} />
          <Route
            exact
            key="restore-homepage"
            path="/restore-homepage"
            component={RestoreHomePage}
          />
          <Route
            exact
            key="jobs"
            path="/jobs"
            render={() => window.location.replace(HATCH_LEVER_JOBS_URL)}
          />
          <Redirect key="redirect-terms" from="/pages/terms" to="/terms" />
          <Redirect
            key="redirect-privacy"
            from="/pages/privacy(-policy)?"
            to="/privacy-policy"
          />
          <Route
            key="support"
            path="/support(/)?"
            render={() => window.location.replace(ZENDESK_SUPPORT_PAGE_URL)}
          />
          <Route
            key="grow"
            path="/pages/grow"
            render={() =>
              window.location.replace("https://shop.hatchbaby.com/pages/grow")
            }
          />
          <Route
            key="wiggly"
            path="/pages/wiggly"
            render={() =>
              window.location.replace("https://shop.hatchbaby.com/pages/wiggly")
            }
          />
          <Route
            key="hatch-sleep-membership"
            path="/hatch-sleep-membership"
            component={HatchMembershipPage}
          />
          <Route
            key="rest-family"
            path="/rest-family"
            component={RestFamilyPage}
          />
          <Route
            key="rest-second-gen"
            path="/restsecondgen"
            component={Rest2ndGenPage}
          />
          <Route key="rest" path="/rest" component={Rest1stGenPage} />
          <Route key="rest-plus" path="/rest-plus" component={RestPlusPage} />
          <Route
            key="accessories"
            path="/accessories/powercords"
            component={AccessoriesPage}
          />
          <Route
            key="linkedin-wellness"
            path="/linkedin-wellness"
            component={LinkedInCorporateWellnessPage}
          />
          <Route
            key="hatch-for-work"
            path="/hatch-for-work"
            component={HatchForWorkPage}
          />
          <Route key="about-us" path="/about-us" component={AboutUsPage} />
          <Route key="careers" path="/careers" component={CareersPage} />
          <Route
            key="privacy-policy"
            path="/privacy-policy"
            component={PrivacyPage}
          />
          <Route key="terms" path="/terms" component={TermsPage} />
          <Route
            key="reset-password"
            path="/reset-password"
            component={ResetPasswordPage}
          />
          <Route
            key="deep-link-slug"
            path="/l/:deepLinkSlug"
            component={DeepLinkLandingPage}
          />
          ,{additionalRoutes}
          <Route component={CatchAllPage} />
        </Switch>
      </Suspense>
    </LazyLoadErrorBoundary>
  );
};

export default Routes;
